import { firstValueFrom } from "rxjs";
import {
  Recipe,
  Segment,
  DataManagementPlatform,
  RetargetingSegment,
  RetargetingType,
  ServiceStatus,
  FilterTypes,
  ObjType,
  Creative,
  Advertiser,
  Pixel,
  DoNotCare
} from "@madhive/mad-sdk";
import axios from "axios";
import {
  getCampaignDetails,
  getLineItemDetails,
  transformRawLineItemDetails,
  transformRawRootCampaign,
  appendOwnerIdsToShallowCampaign
} from "api/manageCampaigns";
import { recipeToSmithersAudience } from "appReducers/audiencesReducer/utils";
import { Audience } from "types";
import type {
  PartialOption,
  RetargetingSegmentForm
} from "features/ManageAudiences/v2/components/RetargetingDetails/utils";
import type { LineItemFormatted } from "campaign-types";
import { madSDK } from "lib/sdk";
import {
  RECIPE_LOADING_STRATEGY,
  type RecipeLoadingStrategy
} from "./constants";

/**
 * Note: The API returns Recipe objects with additional properties (booleanExpression, maxPrice, minPrice)
 * that aren't defined in the mad-sdk Recipe type. We use DoNotCare type where needed to access these.
 *
 * TODO: Update the mad-sdk Recipe type definition to include these properties so we can remove the type casts.
 */

/**
 * Check if a recipe has an expression with actual targeting data
 * Returns true only if the expression has at least one id or child
 */
export const hasValidExpression = (recipe: Recipe): boolean => {
  if (!recipe.expression) {
    return false;
  }

  // Normalize undefined to empty arrays for checking
  const ids = Array.isArray(recipe.expression.ids) ? recipe.expression.ids : [];
  const children = Array.isArray(recipe.expression.children)
    ? recipe.expression.children
    : [];

  // Only include if there's actual targeting data (at least one id or child)
  // Empty expressions (no ids, no children) are invalid and will cause API errors
  return ids.length > 0 || children.length > 0;
};

/**
 * Check if a recipe has a booleanExpression with actual targeting data
 * Returns true only if the booleanExpression has at least one id or child
 */
export const hasValidBooleanExpression = (recipe: Recipe): boolean => {
  const recipeWithBoolExpr = recipe as DoNotCare;

  if (!recipeWithBoolExpr.booleanExpression) {
    return false;
  }

  const boolExpr = recipeWithBoolExpr.booleanExpression;

  // Normalize undefined to empty arrays for checking
  const ids = Array.isArray(boolExpr.ids) ? boolExpr.ids : [];
  const children = Array.isArray(boolExpr.children) ? boolExpr.children : [];

  // Only include if there's actual targeting data (at least one id or child)
  // Empty expressions (no ids, no children) are invalid and will cause API errors
  return ids.length > 0 || children.length > 0;
};

/**
 * Build a clean recipe object from the current recipe, explicitly setting all fields
 * to avoid hidden properties from API responses
 */
export const buildCleanRecipe = (
  currentRecipe: Recipe,
  overrides: Partial<Recipe> = {}
): Recipe => {
  // Cast to DoNotCare to access properties that exist at runtime but not in type definition
  const extendedRecipe = currentRecipe as DoNotCare;

  // Build a completely fresh object
  const recipeToSave = {} as DoNotCare;

  // Explicitly set each property
  recipeToSave.id = currentRecipe.id;
  recipeToSave.name = overrides.name ?? currentRecipe.name;
  recipeToSave.type = currentRecipe.type;
  recipeToSave.parent = currentRecipe.parent;
  recipeToSave.category = currentRecipe.category;
  recipeToSave.cpm = currentRecipe.cpm;
  recipeToSave.price = currentRecipe.price;
  recipeToSave.description = currentRecipe.description;
  recipeToSave.householdReach =
    overrides.householdReach ?? currentRecipe.householdReach;
  recipeToSave.status = overrides.status ?? currentRecipe.status;
  recipeToSave.createdOn = currentRecipe.createdOn;
  recipeToSave.createdBy = currentRecipe.createdBy;
  recipeToSave.preferred = overrides.preferred ?? currentRecipe.preferred;
  recipeToSave.maxPrice = extendedRecipe.maxPrice;
  recipeToSave.minPrice = extendedRecipe.minPrice;

  // Only include expression/booleanExpression if they contain valid targeting data
  // Empty expressions cause API validation errors
  if (currentRecipe.expression && hasValidExpression(currentRecipe)) {
    const expr = currentRecipe.expression;
    recipeToSave.expression = {
      ...expr,
      // Normalize undefined to empty arrays to prevent SDK crashes
      ids: Array.isArray(expr.ids) ? expr.ids : [],
      children: Array.isArray(expr.children) ? expr.children : []
    };
  }

  if (
    extendedRecipe.booleanExpression &&
    hasValidBooleanExpression(currentRecipe)
  ) {
    const boolExpr = extendedRecipe.booleanExpression;
    recipeToSave.booleanExpression = {
      ...boolExpr,
      // Normalize undefined to empty arrays to prevent SDK crashes
      ids: Array.isArray(boolExpr.ids) ? boolExpr.ids : [],
      children: Array.isArray(boolExpr.children) ? boolExpr.children : []
    };
  }

  return recipeToSave as Recipe;
};

/**
 * Check if the current cache can satisfy the requested loading strategy
 * FULL cache can satisfy both FULL and MINIMAL requests
 * MINIMAL cache can only satisfy MINIMAL requests
 */
export const canCacheSatisfyStrategy = (
  currentStrategy: RecipeLoadingStrategy | undefined,
  requestedStrategy: RecipeLoadingStrategy
): boolean => {
  if (!currentStrategy) return false;
  // FULL cache can satisfy any request; MINIMAL can only satisfy MINIMAL
  return (
    currentStrategy === requestedStrategy ||
    currentStrategy === RECIPE_LOADING_STRATEGY.FULL
  );
};

/**
 * Check if an update object only contains a preference change
 * Used to determine if we can use the optimized updatePreference endpoint
 */
export const isPreferenceOnlyUpdate = (updates: Partial<Recipe>): boolean => {
  return (
    Object.keys(updates).length === 1 &&
    "preferred" in updates &&
    typeof updates.preferred === "boolean"
  );
};

/**
 * Update a cache item (recipe or segment) immutably
 * Creates new cache object with updated item, preserving other items
 */
export const updateCacheItem = <T extends { id: string }>(
  cache: Record<string, T> | undefined,
  item: T
): Record<string, T> => {
  if (!cache) {
    return { [item.id]: item };
  }
  // Only the updated item property changes - other items unchanged
  return { ...cache, [item.id]: item };
};

/**
 * Convert an array of items with IDs to a keyed object (byId pattern)
 * Used for converting API array responses to Redux-style normalized state
 */
export const convertArrayToKeyedObject = <T extends { id: string }>(
  items: T[]
): Record<string, T> => {
  return (items || []).reduce(
    (acc, item) => {
      acc[item.id] = item;
      return acc;
    },
    {} as Record<string, T>
  );
};

/**
 * Transform recipes object to audiences object
 * Converts from backend Recipe format to frontend Audience format
 */
export const transformRecipesToAudiences = (
  recipes: Record<string, Recipe> | undefined
): Record<string, Audience> => {
  if (!recipes) return {};

  return Object.entries(recipes).reduce(
    (acc, [id, recipe]) => {
      acc[id] = recipeToSmithersAudience(recipe);
      return acc;
    },
    {} as Record<string, Audience>
  );
};

/**
 * Filter segments to only include READY or EXPIRED status
 * Excludes ARCHIVED, DRAFT, PAUSED, etc.
 */
export const filterSegmentsByStatus = (
  segments: Record<string, Segment> | undefined
): Record<string, Segment> => {
  if (!segments) return {};

  // Only include segments that are visible to users: READY, EXPIRED, and DRAFT (pending).
  // DRAFT segments are shown in the segment list so users can see pending status,
  // but are blocked from being added to audiences at the backend level.
  // READY=200, EXPIRED=900, DRAFT=100 (excludes ARCHIVED=500, etc.)
  return Object.entries(segments).reduce(
    (acc, [id, segment]) => {
      if (
        segment.status === ServiceStatus.READY ||
        segment.status === ServiceStatus.EXPIRED ||
        segment.status === ServiceStatus.DRAFT
      ) {
        acc[id] = segment;
      }
      return acc;
    },
    {} as Record<string, Segment>
  );
};

/**
 * Transform DMPs array to keyed object by ID
 */
export const transformDmpsToKeyedObject = (
  dmps: DataManagementPlatform[] | undefined
): Record<string, DataManagementPlatform> => {
  if (!dmps) return {};

  return dmps.reduce(
    (acc, dmp) => {
      acc[dmp.id] = dmp;
      return acc;
    },
    {} as Record<string, DataManagementPlatform>
  );
};

/**
 * Hydrate a segment after update with fields that the API doesn't return
 * The API doesn't return label, description, preferred, createdOn, createdBy
 * so we need to add them back from the original segment
 */
export const hydrateSegmentAfterUpdate = (
  updatedSegment: Segment,
  originalSegment: Segment,
  updates: { label?: string; description?: string; preferred?: boolean }
): Segment => {
  return {
    ...updatedSegment,
    label: updates.label ?? updatedSegment.label,
    description: updates.description ?? updatedSegment.description,
    preferred: updates.preferred ?? updatedSegment.preferred,
    createdOn: originalSegment.createdOn,
    createdBy: originalSegment.createdBy
  };
};

/**
 * Mint a new ID for an audience (recipe)
 * Throws error if minting fails
 */
export const mintAudienceId = async (): Promise<string> => {
  const id = await madSDK.cryptography.mintKey(ObjType.SEG);
  if (!id) {
    throw new Error("Failed to mint ID for new audience");
  }
  return id;
};

/**
 * Hydrate pixel targets for a retargeting segment
 * Fetches full Pixel objects from IDs, filtering for READY and ARCHIVED
 */
const hydratePixelTargets = async (targetIds: string[]): Promise<Pixel[]> => {
  const pixels = await madSDK.pixels.find({
    where: [
      {
        field: "id",
        type: FilterTypes.IN,
        value: targetIds
      },
      {
        field: "status",
        type: FilterTypes.IN,
        value: [ServiceStatus.READY, ServiceStatus.ARCHIVED]
      }
    ]
  });
  return (pixels || []).filter((pixel: Pixel) => pixel);
};

/**
 * Hydrate creative targets for a retargeting segment
 * Fetches full Creative objects from IDs, filtering for READY and ARCHIVED
 */
const hydrateCreativeTargets = async (
  targetIds: string[]
): Promise<Creative[]> => {
  const creativesResponse = await firstValueFrom(
    madSDK.creatives.find({
      where: [
        {
          field: "id",
          type: FilterTypes.IN,
          value: targetIds
        },
        {
          field: "status",
          type: FilterTypes.IN,
          value: [ServiceStatus.READY, ServiceStatus.ARCHIVED]
        }
      ]
    })
  );

  // Handle different response formats from the SDK
  let creatives: Creative[] = [];
  if (Array.isArray(creativesResponse)) {
    creatives = creativesResponse;
  } else if (creativesResponse && "data" in creativesResponse) {
    creatives = creativesResponse.data;
  } else if (creativesResponse) {
    creatives = [creativesResponse];
  }

  return creatives.filter(creative => creative != null);
};

/**
 * Hydrate campaign targets for a retargeting segment
 * Fetches full campaign details with advertiser/agency ownership info
 * Returns DoNotCare because the result includes additional properties beyond RootCampaign
 * (advertiserId, agencyId, ownerIdsWithTypes, stationIds)
 */
const hydrateCampaignTargets = async (
  targetIds: string[]
): Promise<DoNotCare[]> => {
  return Promise.all(
    targetIds.map(async id => {
      const cancellationSource = axios.CancelToken.source();
      const campaign = await getCampaignDetails(id, cancellationSource, {
        includeArchived: true
      });
      const formattedRootCampaign = transformRawRootCampaign(campaign);
      const { advertiserId, agencyId, ownerIdsWithTypes, stationIds } =
        await appendOwnerIdsToShallowCampaign(
          formattedRootCampaign.ownerIdsMadhiveEncrypted,
          formattedRootCampaign.parent
        );
      return {
        ...formattedRootCampaign,
        advertiserId,
        agencyId,
        ownerIdsWithTypes,
        stationIds
      };
    })
  );
};

/**
 * Hydrate line item targets for a retargeting segment
 * Fetches full line item details including archived items
 */
const hydrateLineItemTargets = async (
  targetIds: string[]
): Promise<LineItemFormatted[]> => {
  return Promise.all(
    targetIds.map(async id => {
      const cancellationSource = axios.CancelToken.source();
      const lineItem = await getLineItemDetails(id, cancellationSource, true);
      return transformRawLineItemDetails(lineItem, {});
    })
  );
};

/**
 * Report ids carry no name of their own, so they are resolved against the owning
 * org's dataset catalog and fall back to the raw id when it no longer lists one.
 */
const hydrateMeasurementTargets = async (
  segment: RetargetingSegment
): Promise<PartialOption[]> => {
  const reportIds = segment.target || [];
  if (!reportIds.length) {
    return [];
  }

  let namesByReportId = new Map<string, string>();
  try {
    const datasets = await madSDK.measurementDatasets.find({
      where: [{ field: "id", type: FilterTypes.EQ, value: segment.parent }]
    });
    namesByReportId = new Map(
      datasets.map(dataset => [dataset.reportId, dataset.name])
    );
  } catch {
    // Names are a nicety here; the ids below still identify the targets.
  }

  return reportIds.map(reportId => ({
    id: reportId,
    name: namesByReportId.get(reportId) || reportId
  }));
};

/**
 * Fetch and hydrate segment targets based on retargeting type
 * Dispatches to appropriate hydration function based on segment type
 */
export const fetchSegmentTargets = async (
  segment: RetargetingSegment
): Promise<RetargetingSegmentForm["target"]> => {
  switch (segment.retargetingType) {
    case RetargetingType.TRACKING_PIXELS:
      return hydratePixelTargets(segment.target);
    case RetargetingType.CREATIVES:
      return hydrateCreativeTargets(segment.target);
    case RetargetingType.CAMPAIGNS:
      return hydrateCampaignTargets(segment.target);
    case RetargetingType.LINE_ITEMS:
      return hydrateLineItemTargets(segment.target);
    case RetargetingType.MEASUREMENT:
      return hydrateMeasurementTargets(segment);
    default:
      return [];
  }
};

/**
 * Extract advertiser ID from a hydrated target object
 * Handles different target types (pixel, creative, campaign, line item)
 */
const extractAdvertiserId = async (
  target: DoNotCare
): Promise<string | undefined> => {
  // Direct advertiserId property (creatives, campaigns)
  if ("advertiserId" in target) {
    return target.advertiserId;
  }

  // Line items need parent campaign lookup
  if ("parentCampaignId" in target) {
    const cancellationSource = axios.CancelToken.source();
    const rawCampaign = await getCampaignDetails(
      target.parentCampaignId,
      cancellationSource,
      { includeArchived: true }
    );
    const formattedRootCampaign = transformRawRootCampaign(rawCampaign);
    const { advertiserId } = await appendOwnerIdsToShallowCampaign(
      formattedRootCampaign.ownerIdsMadhiveEncrypted,
      formattedRootCampaign.parent
    );
    return advertiserId;
  }

  return undefined;
};

/**
 * Fetch advertiser for a retargeting segment based on its first target
 * Returns undefined if no targets or advertiser can't be found
 */
export const fetchAdvertiserForSegment = async (
  hydratedTargets: RetargetingSegmentForm["target"]
): Promise<Advertiser | undefined> => {
  const firstTarget = hydratedTargets[0];
  if (!firstTarget) return undefined;

  const advertiserId = await extractAdvertiserId(firstTarget);
  if (!advertiserId) return undefined;

  const advertisers = await madSDK.advertisers.find({
    where: [{ field: "id", type: FilterTypes.EQ, value: advertiserId }]
  });

  return advertisers?.[0];
};

/**
 * Build RetargetingSegmentForm from segment data and hydrated targets
 * Combines segment metadata with hydrated target objects and advertiser
 */
export const buildRetargetingSegmentForm = (
  segment: RetargetingSegment,
  hydratedTargets: RetargetingSegmentForm["target"],
  advertiser: Advertiser | undefined
): RetargetingSegmentForm => {
  return {
    name: segment.label,
    notes: segment.description,
    advertiser,
    retargetingType: segment.retargetingType,
    target: hydratedTargets,
    eventType: segment.eventType,
    eventSubtype: segment.eventSubtype,
    parameterFilters: segment.parameterFilters,
    parameterFilterOperator: segment.parameterFilterOperator
  };
};
